import { useState, useRef, useEffect } from "react";
import { API_BASE } from '@/lib/api';
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, Loader2, ArrowLeft, CheckCircle2, CreditCard, MessageCircle, Home, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SavedCardsSelector from "@/components/SavedCardsSelector";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const API_URL = API_BASE;

const CleaningBookingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [bookingReady, setBookingReady] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState("new");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  useEffect(() => {
    // Only scroll within the messages container, not the whole page
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.parentElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [messages]);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 300);
  }, []);

  useEffect(() => {
    const initialMessage = {
      role: "assistant",
      content: "Hello! Welcome to Stack's Cleaning Service. I'm here to help you book professional cleaning for your home or office.\n\nWhether you need a regular tidy-up, deep clean, or move-in/out service, we've got you covered. To get started, what type of cleaning are you looking for today?"
    };
    setMessages([initialMessage]);
  }, []);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = { role: "user", content: inputValue };
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/cleaning/chat`, {
        session_id: sessionId,
        message: inputValue,
        user_email: user?.email || "guest@stack.com",
        user_name: user?.name || "Guest",
        user_phone: user?.phone || null,
        user_address: user?.address || null
      });

      setSessionId(response.data.session_id);
      
      let displayText = response.data.response;
      displayText = displayText.replace(/```json[\s\S]*?```/g, "").trim();
      
      const assistantMessage = { role: "assistant", content: displayText };
      setMessages(prev => [...prev, assistantMessage]);

      if (response.data.booking_ready && response.data.booking_data) {
        setBookingReady(true);
        setBookingData(response.data.booking_data);
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Failed to send message. Please try again.");
      setMessages(prev => prev.slice(0, -1));
      setInputValue(userMessage.content);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleConfirmBooking = async () => {
    if (!bookingData || !sessionId) return;
    
    setIsBooking(true);
    try {
      // Always use profile data for contact info (guaranteed valid)
      const bookingPayload = {
        session_id: sessionId,
        cleaning_type: bookingData.cleaning_type || "regular",
        property_type: bookingData.property_type || "house",
        square_footage: parseInt(bookingData.square_footage) || 1000,
        num_bedrooms: bookingData.num_bedrooms ? parseInt(bookingData.num_bedrooms) : null,
        num_bathrooms: bookingData.num_bathrooms ? parseInt(bookingData.num_bathrooms) : null,
        service_date: bookingData.service_date,
        service_time: bookingData.service_time,
        frequency: bookingData.frequency || "one_time",
        location: bookingData.location || user?.address || "Not specified",
        customer_name: user?.name || bookingData.customer_name || "Guest",
        customer_phone: user?.phone || bookingData.customer_phone || "Not provided",
        customer_email: user?.email || "guest@stack.com",
        focus_areas: bookingData.focus_areas !== "none" ? bookingData.focus_areas : null,
        has_pets: bookingData.has_pets || false,
        special_requests: bookingData.special_requests !== "none" ? bookingData.special_requests : null
      };

      console.log("Booking payload:", bookingPayload);
      const response = await axios.post(`${API_URL}/api/cleaning/booking`, bookingPayload);
      
      toast.success("Booking confirmed! Processing payment...");
      
      // Calculate price based on inputs
      const sqft = bookingPayload.square_footage || 1000;
      let ratePerSqft = 0.12; // Default regular rate
      let minimum = 80;
      
      const cleaningType = bookingPayload.cleaning_type?.toLowerCase() || "regular";
      if (cleaningType === "deep") {
        ratePerSqft = 0.25;
        minimum = 150;
      } else if (cleaningType === "move_in_out" || cleaningType === "move-in/out") {
        ratePerSqft = 0.30;
        minimum = 200;
      } else if (cleaningType === "post_construction") {
        ratePerSqft = 0.35;
        minimum = 250;
      } else if (cleaningType === "office") {
        ratePerSqft = 0.10;
        minimum = 100;
      }
      
      let totalPrice = Math.max(sqft * ratePerSqft, minimum);
      
      // Apply recurring discounts
      const frequency = bookingPayload.frequency || "one_time";
      if (frequency === "weekly") totalPrice *= 0.85;
      else if (frequency === "bi_weekly") totalPrice *= 0.90;
      else if (frequency === "monthly") totalPrice *= 0.95;
      
      totalPrice = Math.round(totalPrice);
      
      // Pay with saved card or redirect to checkout using pre-auth hold
      const serviceData = {
        booking_id: response.data.id,
        booking_type: "cleaning",
        amount: totalPrice,
        customer_email: bookingPayload.customer_email,
        customer_name: bookingPayload.customer_name,
        service_type: "cleaning",
        pickup_address: bookingPayload.location || "",
        dropoff_address: "",
      };

      if (selectedCardId && selectedCardId !== "new") {
        const authResponse = await axios.post(`${API_URL}/api/payments/authorize-hold`, {
          ...serviceData,
          payment_method_id: selectedCardId,
        }, { withCredentials: true });
        if (authResponse.data.success) {
          toast.success("Payment authorized! Finding your cleaner...");
          navigate("/");
        } else {
          toast.error("Payment authorization failed. Please try again.");
        }
        setIsBooking(false);
      } else {
        const paymentResponse = await axios.post(`${API_URL}/api/payments/create-checkout`, {
          ...serviceData,
          origin_url: window.location.origin,
        });
        window.location.href = paymentResponse.data.checkout_url;
      }
    } catch (error) {
      console.error("Booking error:", error);
      console.error("Error response:", error.response?.data);
      let errorMsg = "Failed to create booking. Please try again.";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error(errorMsg);
    } finally {
      setIsBooking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white" data-testid="cleaning-booking-page">
      <Navbar />

      <section className="pt-28 pb-8 px-4 md:px-0">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="mb-4 text-stone-600 hover:text-cyan-700"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-emerald-950">Cleaning Service</h1>
              <p className="text-stone-600">Professional cleaning for homes & offices</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-8">
            {[
              { icon: MessageCircle, text: "Custom Quotes" },
              { icon: Shield, text: "Insured Cleaners" },
              { icon: Home, text: "All Property Types" }
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-stone-600">
                <feature.icon className="w-4 h-4 text-cyan-500" />
                <span>{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 px-4 md:px-0">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl border border-stone-200 overflow-hidden" data-testid="chat-container">
            <div className="bg-gradient-to-r from-cyan-600 to-teal-600 px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Cleaning Coordinator</h3>
                <p className="text-xs text-cyan-100">Your Cleaning Service Assistant</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-xs text-cyan-100">Online</span>
              </div>
            </div>

            <div className="h-[450px] overflow-y-auto p-6 space-y-4 bg-stone-50" data-testid="messages-area">
              <AnimatePresence>
                {messages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        message.role === "user"
                          ? "bg-cyan-600 text-white rounded-br-md"
                          : "bg-white text-stone-800 shadow-sm border border-stone-100 rounded-bl-md"
                      }`}
                      data-testid={`message-${message.role}`}
                    >
                      <p className="whitespace-pre-wrap text-sm md:text-base">{message.content}</p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-stone-100">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-cyan-500" />
                      <span className="text-sm text-stone-500">Cleaning Coordinator is typing...</span>
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {bookingReady && bookingData && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="bg-gradient-to-r from-cyan-50 to-teal-50 border-t border-cyan-200 p-6"
                data-testid="booking-confirmation"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-emerald-950 mb-2">Ready to Confirm Your Booking!</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-stone-600 mb-4">
                      <div><strong>Date:</strong> {bookingData.service_date}</div>
                      <div><strong>Time:</strong> {bookingData.service_time}</div>
                      <div><strong>Type:</strong> {bookingData.cleaning_type?.replace("_", " ")}</div>
                      <div><strong>Size:</strong> {bookingData.square_footage} sq ft</div>
                    </div>
                    <SavedCardsSelector
                      email={user?.email}
                      name={user?.name}
                      onCardSelect={setSelectedCardId}
                      selectedCardId={selectedCardId}
                      disabled={isBooking}
                    />
                    <Button
                      onClick={handleConfirmBooking}
                      disabled={isBooking}
                      className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
                      data-testid="confirm-booking-btn"
                    >
                      {isBooking ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Confirm & Pay
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            <form onSubmit={sendMessage} className="border-t border-stone-200 p-4 bg-white">
              <div className="flex gap-3">
                <Input
                  ref={inputRef}
                  autoFocus
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Tell us about your cleaning needs..."
                  className="flex-1 rounded-full border-stone-300 focus:border-cyan-400 focus:ring-cyan-400"
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  disabled={!inputValue.trim() || isLoading}
                  className="rounded-full bg-cyan-600 hover:bg-cyan-700 text-white px-6"
                  data-testid="send-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-stone-400 mt-2 text-center">
                All cleaners are vetted, insured, and professionally trained
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CleaningBookingPage;
